import type { ResolutionContext } from '../domain';
import { resolveBinding } from '../domain/bindingResolver';

type ResolveDatafieldParams = {
  property: Properties;
  binding: DataBinding | undefined;
  data: unknown;
  context?: ResolutionContext;
};

type ResolveRepeaterParams = {
  repeater: RepeaterProperties;
  repeaterBinding: DataBinding | undefined;
  allProperties: Properties[];
  data: unknown;
  rootData: unknown;
};

type ResolveDatafieldsParams = {
  properties: Properties[];
  data: unknown;
};

type ResolvedPropertyData = {
  propertyValues: PropertyValue;
  errors: Record<string, string>;
};

type RootContextPath = {
  usesRootContext: boolean;
  resolvedPath: string;
};

const ROOT_CONTEXT_PREFIX = '$root.';

function parseRootContextPath(path: string): RootContextPath {
  const usesRootContext = path.startsWith(ROOT_CONTEXT_PREFIX);
  const resolvedPath = usesRootContext ? path.substring(ROOT_CONTEXT_PREFIX.length) : path;

  return {
    usesRootContext,
    resolvedPath,
  };
}

async function resolveDatafield(
  params: ResolveDatafieldParams
): Promise<{ value: string; error?: string }> {
  const { property, binding, data, context } = params;

  if (!binding) {
    return {
      value: property.defaultValue ?? '',
      error: undefined,
    };
  }

  const result = await resolveBinding({
    binding,
    data,
    context,
  });

  if (result.error) {
    const fallbackValue = property.defaultValue ?? '';
    return {
      value: fallbackValue,
      error: result.error,
    };
  }

  return {
    value: String(result.value ?? ''),
    error: undefined,
  };
}

function validateArrayData(data: unknown): { valid: boolean; error?: string } {
  if (!Array.isArray(data)) {
    return {
      valid: false,
      error: 'Repeater binding did not resolve to an array',
    };
  }

  return { valid: true };
}

async function resolveChildProperty(params: {
  child: Properties;
  childBinding: DataBinding | undefined;
  itemData: unknown;
  rootData: unknown;
  context: ResolutionContext;
  allProperties: Properties[];
}): Promise<{ value: string | PropertyValue[]; error?: string }> {
  const { child, childBinding, itemData, rootData, context, allProperties } = params;

  if (!childBinding) {
    return {
      value: child.defaultValue ?? '',
      error: undefined,
    };
  }

  if (child.type === 'repeater') {
    const nestedResult = await resolveRepeater({
      repeater: child,
      repeaterBinding: childBinding,
      allProperties,
      data: itemData,
      rootData,
    });

    return {
      value: nestedResult.error ? [] : nestedResult.items,
      error: nestedResult.error,
    };
  }

  const { usesRootContext, resolvedPath } = parseRootContextPath(childBinding.path);
  const dataForResolution = usesRootContext ? rootData : itemData;

  const result = await resolveBinding({
    binding: { path: resolvedPath },
    data: dataForResolution,
    context,
  });

  if (result.error) {
    return {
      value: child.defaultValue ?? '',
      error: result.error,
    };
  }

  return {
    value: String(result.value ?? ''),
    error: undefined,
  };
}

async function resolveRepeaterItem(params: {
  repeater: RepeaterProperties;
  itemData: unknown;
  rootData: unknown;
  allProperties: Properties[];
  context: ResolutionContext;
}): Promise<PropertyValue> {
  const { repeater, itemData, rootData, allProperties, context } = params;
  const itemValues: PropertyValue = {};

  const resolutionPromises = repeater.children.map(async (child) => {
    const childBinding = child.dataBinding;

    if (!childBinding) {
      return {
        childId: child.id,
        value: child.defaultValue,
      };
    }

    const childResult = await resolveChildProperty({
      child,
      childBinding,
      itemData,
      rootData,
      context,
      allProperties,
    });

    return {
      childId: child.id,
      value: childResult.value,
    };
  });

  const results = await Promise.all(resolutionPromises);

  for (const result of results) {
    if (result.value !== undefined) {
      itemValues[result.childId] = result.value;
    }
  }

  return itemValues;
}

async function resolveRepeater(params: ResolveRepeaterParams): Promise<{
  items: PropertyValue[];
  error?: string;
}> {
  const { repeater, repeaterBinding, allProperties, data, rootData } = params;

  if (!repeaterBinding) {
    return {
      items: [],
      error: 'Repeater has no binding',
    };
  }

  const repeaterResult = await resolveBinding({
    binding: repeaterBinding,
    data,
  });

  if (repeaterResult.error) {
    return {
      items: [],
      error: repeaterResult.error,
    };
  }

  const arrayValidation = validateArrayData(repeaterResult.value);
  if (!arrayValidation.valid) {
    return {
      items: [],
      error: arrayValidation.error,
    };
  }

  const arrayData = repeaterResult.value as unknown[];

  const itemPromises = arrayData.map((itemData, index) => {
    const context: ResolutionContext = { $index: index };

    return resolveRepeaterItem({
      repeater,
      itemData,
      rootData,
      allProperties,
      context,
    });
  });

  const items = await Promise.all(itemPromises);

  return {
    items,
    error: undefined,
  };
}

export const resolveDatafields = async (
  params: ResolveDatafieldsParams
): Promise<ResolvedPropertyData> => {
  const { properties, data } = params;

  const propertyPromises = properties.map(async (property) => {
    if (property.type === 'repeater') {
      const repeaterBinding = property.dataBinding;

      const repeaterResult = await resolveRepeater({
        repeater: property,
        repeaterBinding,
        allProperties: properties,
        data,
        rootData: data,
      });

      return {
        propertyId: property.id,
        value: repeaterResult.items,
        error: repeaterResult.error,
      };
    } else {
      const binding = property.dataBinding;

      const scalarResult = await resolveDatafield({
        property,
        binding,
        data,
      });

      return {
        propertyId: property.id,
        value: scalarResult.value,
        error: scalarResult.error,
      };
    }
  });

  const results = await Promise.all(propertyPromises);

  const propertyValues: PropertyValue = {};
  const errors: Record<string, string> = {};

  for (const result of results) {
    propertyValues[result.propertyId] = result.value;

    if (result.error) {
      errors[result.propertyId] = result.error;
    }
  }

  return {
    propertyValues,
    errors,
  };
};
